/**
 * Date utility functions for handling timezone-sensitive date parsing
 */

/**
 * Parses a date string ensuring that date-only strings (YYYY-MM-DD) are treated as local midnight
 * instead of UTC midnight, preventing unwanted timezone conversions.
 * 
 * @param {string|Date} dateString - The date string or Date object to parse
 * @returns {Date} A Date object representing the local time
 * 
 * Examples:
 * - "2025-01-15" -> Jan 15, 2025 00:00:00 (local time)
 * - "2025-01-15 12:14:44" -> Jan 15, 2025 12:14:44 (local time)
 * - "2025-01-15T12:14:44Z" -> Jan 15, 2025 converted from UTC (preserves timezone info)
 */
export const parseEventDate = (dateString) => {
    if (!dateString) {
        return new Date(NaN); // Invalid date
    }

    // If already a Date object, return it
    if (dateString instanceof Date) {
        return dateString;
    }

    // Convert to string
    const dateStr = String(dateString).trim();

    // Check if it's a date-only string (YYYY-MM-DD format without time)
    // This regex matches: YYYY-MM-DD or YYYY/MM/DD (with optional leading zeros)
    const dateOnlyPattern = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/;
    
    if (dateOnlyPattern.test(dateStr)) {
        // Parse as local date by appending "T00:00:00" to force local timezone interpretation
        // This prevents the UTC midnight interpretation that causes date shifts
        const normalizedDate = dateStr.replace(/\//g, '-') + 'T00:00:00';
        return new Date(normalizedDate);
    }

    // Check if it's a date with time but no timezone (YYYY-MM-DD HH:MM:SS)
    const dateTimePattern = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}\s+\d{1,2}:\d{1,2}(:\d{1,2})?$/;
    
    if (dateTimePattern.test(dateStr)) {
        // Replace space with 'T' to create ISO format, ensuring local interpretation
        const normalizedDate = dateStr.replace(/\//g, '-').replace(' ', 'T');
        return new Date(normalizedDate);
    }

    // For dates with timezone info (Z, +HH:MM, etc.), use standard parsing
    return new Date(dateStr);
};

/**
 * Formats a Date object to ISO string format suitable for datetime-local input
 * 
 * @param {Date} date - The date to format
 * @returns {string} ISO string in format YYYY-MM-DDTHH:MM
 */
export const formatForDateTimeLocal = (date) => {
    if (!date || isNaN(date.getTime())) {
        return "";
    }
    
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formats a Date object to ISO date string (YYYY-MM-DD)
 * 
 * @param {Date} date - The date to format
 * @returns {string} ISO date string
 */
export const formatToISODate = (date) => {
    if (!date || isNaN(date.getTime())) {
        return "";
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};

/**
 * Formats a timestamp (ms, ISO string, or other date-like) into a localized display string.
 * Timezone-naive ISO strings are UTC by default; set assumeUtcForNaive=false for local wall times.
 */
export const formatTimestamp = (timestamp, options = {}) => {
    if (!timestamp && timestamp !== 0) return '';
    const { assumeUtcForNaive = true, ...localeOptions } = options;

    const normalizeTimestamp = (value) => {
        if (typeof value === 'number') return value;
        const raw = String(value).trim();
        if (!raw) return null;

        if (/^\d+$/.test(raw)) return parseInt(raw, 10);
        const hasTZ = /[Zz]|[+-]\d{2}(:?\d{2})?$/.test(raw);
        let candidate = raw;
        if (!hasTZ && assumeUtcForNaive) {
            if (/\s+UTC$/i.test(candidate)) {
                candidate = candidate.replace(/\s+UTC$/i, 'Z');
            } else if (!candidate.endsWith('Z')) {
                candidate = `${candidate}Z`;
            }
        }
        return candidate;
    };

    try {
        const normalized = normalizeTimestamp(timestamp);
        const date = assumeUtcForNaive
            ? (normalized ? new Date(normalized) : new Date(timestamp))
            : (timestamp instanceof Date
                ? timestamp
                : typeof normalized === 'number'
                    ? new Date(normalized)
                    : parseEventDate(normalized || timestamp));
        if (Number.isNaN(date.getTime())) return String(timestamp);
        const defaultOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        };
        return date.toLocaleString(undefined, { ...defaultOptions, ...localeOptions });
    } catch (err) {
        return String(timestamp);
    }
};
